import React, { useCallback } from 'react'
import { Box, Button, Chip, Divider, Stack, Typography, darken, useTheme } from '@mui/material'
import { DeleteOutline, Undo } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useRestorePosition, useRevertPosition, useUpdatePositionCoordinates } from 'src/api/projectVersionPositions'
import { useDeleteProjectPosition } from 'src/api/projectPosition'
import { useGetProjectForMap } from 'src/api/project'
import { formatVersionLabel } from './projectVersion.action'
import { formatPosition } from './coordinates.action'
import { IPosition, IPositionType, IPositionWithDrawing } from 'src/generated-types'
import { IChangeType } from 'src/generated-types/project-version-changes'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { GetRGBA } from 'src/utils'
import { CHANGE_GLYPH, CHANGE_LABEL_KEY } from './changeLabels'
import CoordinateEditor, { COMPACT_BUTTON_SX } from './CoordinateEditor'
import { KindTag } from './PositionRevisionMarkers'
import { Translate, getChangeColor, publishedNameOf } from './projectVersionChanges.action'
import { refreshProjectData, withRemoved, withoutChanges } from './refreshProjectData'
import { RevisionConfigure } from './useRevisionConfigure'

interface RevisionSelectionPanelProps {
  revision: RevisionConfigure
  customerId: EntityId
  localityId: EntityId
  projectId: EntityId
  activeType: IPositionType
  selectedIds: Set<number>
  positionById: Map<number, IPosition>
  onClear: () => void
}

// What can be done with the selection in a draft: move one position, revert or remove several, restore removed ones.
export const RevisionSelectionPanel: React.FC<RevisionSelectionPanelProps> = ({
  revision,
  customerId,
  localityId,
  projectId,
  activeType,
  selectedIds,
  positionById,
  onClear,
}) => {
  const { t: translate } = useTranslation(['projects'])
  const theme = useTheme()
  const { showConfirmDialog } = useDialogCtx()
  const { changeByName, changes } = revision
  const { mutateAsync: updateCoordinates, isLoading: isMoving } = useUpdatePositionCoordinates(
    customerId,
    localityId,
    projectId,
  )
  const { mutateAsync: revertPosition } = useRevertPosition(customerId, localityId, projectId)
  const { mutateAsync: restorePosition } = useRestorePosition(customerId, localityId, projectId)
  const { mutateAsync: deletePosition } = useDeleteProjectPosition(customerId, projectId)
  // Only the map payload carries connections, which tell the corner a mooring line starts from.
  const { data: mapProject } = useGetProjectForMap(customerId, localityId, projectId)
  const pointOf = (p?: IPosition) =>
    p && (Number(p.latitude) || Number(p.longitude))
      ? { latitude: Number(p.latitude), longitude: Number(p.longitude) }
      : undefined
  const moveContextOf = (position: IPosition) => {
    const isAnchor = position.type === IPositionType.MooringLine
    const cornerId = mapProject?.positions?.find(p => p.id === position.id)?.connectedPositions?.[0]
      ?.positionConnectedId
    return {
      isAnchor,
      published: pointOf(revision.basePositions.find(p => p.name === publishedNameOf(changeByName, position.name))),
      publishedLabel: formatVersionLabel(changes?.baseVersionNumber ?? 0),
      lineStart: isAnchor && cornerId ? pointOf(positionById.get(cornerId)) : undefined,
    }
  }

  const t: Translate = (key, options) => translate(key, { ns: 'projects', ...options }) as string
  const selected = [...selectedIds]
    .map(id => positionById.get(id))
    .filter((p): p is IPosition => !!p && p.type === activeType)
  // Positions of the open tab that the draft removed, taken from the published version.
  const removed = revision.basePositions.filter(p => p.type === activeType && revision.removedByName.has(p.name ?? ''))
  const revertable = selected.filter(p => {
    const change = changeByName.get(p.name ?? '')
    return change != null && change.type !== IChangeType.Added
  })
  const pickedRemoved = removed.filter(p => revision.selectedRemoved.includes(p.name ?? ''))

  const run = useCallback(
    async (action: () => Promise<unknown>, successKey: string, patch?: Parameters<typeof refreshProjectData>[1]) => {
      try {
        await action()
        toast.success(t(successKey))
        refreshProjectData(projectId, patch)
        return true
      } catch {
        toast.error(t('revision-action-failed'))
        return false
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projectId, translate],
  )

  const confirm = (
    title: string,
    message: string,
    action: () => Promise<unknown>,
    successKey: string,
    patch: Parameters<typeof refreshProjectData>[1],
  ) =>
    showConfirmDialog(
      title,
      message,
      { acceptText: translate('yes', { ns: 'common' }), cancelText: translate('no', { ns: 'common' }) },
      async pop => {
        await run(action, successKey, patch)
        pop()
      },
    )

  const onRevert = () =>
    confirm(
      t('revision-revert-title', { version: changes?.baseVersionNumber ?? '' }),
      t('revision-revert-warning', { count: revertable.length }),
      () => Promise.all(revertable.map(p => revertPosition({ positionId: p.id }))),
      'revision-reverted',
      withoutChanges(revertable.map(p => p.name ?? '')),
    )

  const onRemove = () =>
    confirm(
      t('revision-remove-title', { count: selected.length }),
      t('revision-remove-warning'),
      async () => {
        await Promise.all(selected.map(p => deletePosition(p.id)))
        onClear()
      },
      'revision-removed',
      withRemoved(selected.map(p => p.name ?? '')),
    )

  const onRestorePicked = () =>
    run(
      async () => {
        await Promise.all(pickedRemoved.map(p => restorePosition({ name: p.name ?? '' })))
        revision.setSelectedRemoved(revision.selectedRemoved.filter(name => !pickedRemoved.some(p => p.name === name)))
      },
      'revision-restored',
      withoutChanges(
        pickedRemoved.map(p => p.name ?? ''),
        true,
      ),
    )

  const only = selected.length === 1 ? selected[0] : undefined
  const onlyChange = only ? changeByName.get(only.name ?? '') : undefined
  const changedCount = selected.filter(p => changeByName.has(p.name ?? '')).length
  const kindTag = (kind: IChangeType, label: string) => {
    const color = getChangeColor(theme, kind)
    return (
      <KindTag key={label} tint={GetRGBA(color, 0.14)} ink={kind === IChangeType.Modified ? darken(color, 0.3) : color}>
        <span aria-hidden>{CHANGE_GLYPH[kind]}</span>
        {label}
      </KindTag>
    )
  }

  if (selected.length === 0 && removed.length === 0) return null

  const outlinedSx = { ...COMPACT_BUTTON_SX, backgroundColor: 'background.paper' }
  const actions = (
    <>
      {revertable.length > 0 && (
        <Button size="small" variant="outlined" startIcon={<Undo />} onClick={onRevert} sx={outlinedSx}>
          {only ? t('revision-revert') : `${t('revision-revert')} ${revertable.length}`}
        </Button>
      )}
      {activeType !== IPositionType.Corner && (
        <Button
          size="small"
          variant="outlined"
          color="error"
          startIcon={<DeleteOutline />}
          onClick={onRemove}
          sx={outlinedSx}
        >
          {selected.every(p => changeByName.get(p.name ?? '')?.type === IChangeType.Added)
            ? t('revision-discard-new')
            : t('revision-remove')}
        </Button>
      )}
    </>
  )

  return (
    <Stack spacing={1.5} sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
      {selected.length > 0 && (
        <Stack
          spacing={1.25}
          sx={{
            p: 1.5,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '6px',
            bgcolor: 'background.default',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography fontWeight={700}>
              {only ? only.name : t('revision-selected', { count: selected.length })}
            </Typography>
            {only && (
              <Typography fontSize={12} color="text.secondary">
                {(only as IPositionWithDrawing).positionDrawing?.drawingNumber}
              </Typography>
            )}
            <Box flex={1} />
            {only &&
              (onlyChange ? (
                onlyChange.kinds.map(kind => kindTag(kind, t(CHANGE_LABEL_KEY[kind])))
              ) : (
                <Typography fontSize={12} fontWeight={600} color="text.secondary">
                  {t('revision-unchanged')}
                </Typography>
              ))}
            {!only &&
              changedCount > 0 &&
              kindTag(IChangeType.Modified, t('revision-n-changed', { count: changedCount }))}
          </Stack>
          <Divider />
          {only?.type === IPositionType.MooringLine ? (
            <CoordinateEditor
              key={`${only.id}-${only.latitude}-${only.longitude}`}
              title={t('revision-anchor-coordinates')}
              latitude={Number(only.latitude)}
              longitude={Number(only.longitude)}
              saving={isMoving}
              onSave={(latitude, longitude) =>
                run(
                  () => updateCoordinates({ positionId: only.id, latitude, longitude }),
                  'revision-coordinates-updated',
                )
              }
              actions={actions}
              moveContext={moveContextOf(only)}
            />
          ) : (
            <>
              {only && (
                <Stack direction="row" spacing={1.5} alignItems="baseline">
                  <Typography fontSize={12} color="text.secondary">
                    {t('revision-position')}
                  </Typography>
                  <Typography fontSize={13} fontWeight={600}>
                    {formatPosition(pointOf(only)) ?? t('revision-no-coordinates')}
                  </Typography>
                </Stack>
              )}
              <Stack direction="row" spacing={1}>
                {actions}
              </Stack>
            </>
          )}
          <Typography fontSize={12} color="text.secondary">
            {t('revision-components-note')}
          </Typography>
        </Stack>
      )}
      {removed.length > 0 && (
        <Stack spacing={0.75}>
          <Typography variant="caption" fontWeight={700}>
            {t('revision-removed-title')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {removed.map(p => {
              const picked = revision.selectedRemoved.includes(p.name ?? '')
              return (
                <Chip
                  key={p.id}
                  size="small"
                  variant={picked ? 'filled' : 'outlined'}
                  label={p.name}
                  onClick={() => revision.toggleRemoved(p.name ?? '')}
                  sx={{
                    borderColor: getChangeColor(theme, IChangeType.Removed),
                    ...(picked && { backgroundColor: GetRGBA(getChangeColor(theme, IChangeType.Removed), 0.18) }),
                  }}
                />
              )
            })}
          </Box>
          {pickedRemoved.length > 0 && (
            <Button size="small" variant="outlined" onClick={onRestorePicked}>
              {`${t('revision-restore')} ${pickedRemoved.length}`}
            </Button>
          )}
        </Stack>
      )}
    </Stack>
  )
}

export default RevisionSelectionPanel
